<template>
  <div
    class="quest"
    :class="{
      'quest--full-screen': fullScreen,
      'quest--sidebar-expanded': sidebarExpanded
    }"
    ref="container">
    <QuestSidebar
      v-if="!sharedStore.state.questPaused"
      :screens="sharedStore.state.screens"
      :currentScreenIndex="sharedStore.state.currentScreenIndex"
      :fullScreen="fullScreen"
      @sidebar-toggled="handleSidebarToggle"
      @screen-selected="handleScreenSelected"
      @toggle-fullscreen="toggleFullScreen" />

    <div
      v-if="!sharedStore.state.questPaused"
      class="quest__content"
      ref="questContent">
      <div class="quest__container">
        <div class="quest__header">
          <QuestHeader
            :formattedDuration="formattedDuration"
            :questFinished="sharedStore.state.questFinished"
            :pauseRequested="sharedStore.state.pauseRequested"
            :pauseButtonTitle="pauseButtonTitle"
            @pause-click="handlePauseClick" />
        </div>

        <div class="quest__chapter-item">
          <QuestIntro
            v-if="currentScreen.type === 'questIntro'"
            :questName="questName"
            :intro="questIntro"
            :introImageUrl="sharedStore.state.questIntroImageUrl" />

          <ChapterIntro
            v-else-if="currentScreen.type === 'intro'"
            :chapterName="currentItem.name"
            :intro="currentItem.intro"
            :introImageUrl="currentItem.intro_image_url" />

          <QuestChapter
            v-else-if="currentScreen.type === 'workspace'"
            :chapterItem="currentItem"
            :questId="sharedStore.state.questId"
            :fullScreen="fullScreen"
            :isLastChapter="isLastChapter"
            v-on:chapter-finished="handleScreenFinished" />

          <ChapterOutro
            v-else-if="currentScreen.type === 'outro'"
            :isLastChapter="isLastChapter"
            :outro="currentItem.outro"
            :outroImageUrl="currentItem.outro_image_url" />
        </div>
      </div>
    </div>

    <QuestPauseScreensaver
      v-if="sharedStore.state.questPaused"
      :imageUrl="sharedStore.state.questIntroImageUrl"
      :questName="questName"
      @continue="handleResume" />
  </div>
</template>

<script>
import sharedStore from "components/quest/shared-store";
import Logo from "@images/svgs/header_logo.svg";
import ExpandIcon from "@images/font_awesome/expand-arrows-alt-light.svg";
import ShrinkIcon from "@images/font_awesome/compress-arrows-alt-light.svg";
import QuestChapter from "components/quest/chapter";
import ChapterIntro from "components/quest/chapter-intro";
import ChapterOutro from "components/quest/chapter-outro";
import QuestSidebar from "components/quest/sidebar";
import Sentry from "modules/sentry";
import QuestIntro from "components/quest/quest-intro";
import QuestPauseScreensaver from "components/quest/quest-pause-screensaver";
import QuestHeader from "components/quest/quest-header";

export default {
  components: {
    Logo,
    ExpandIcon,
    ShrinkIcon,
    QuestChapter,
    ChapterIntro,
    ChapterOutro,
    QuestSidebar,
    QuestIntro,
    QuestPauseScreensaver,
    QuestHeader
  },
  data() {
    return {
      sharedStore,
      fullScreen: false,
      currentTime: new Date(),
      stopwatchInterval: null,
      sidebarExpanded: false
    }
  },
  computed: {
    formattedDuration() {
      if (this.sharedStore.state.questFinished) {
        return;
      }

      let duration = this.sharedStore.state.currentDurationSec + this.sharedStore.state.hintPenaltySec;

      if (this.sharedStore.state.currentChapterStartedAt) {
        duration += (this.currentTime - this.sharedStore.state.currentChapterStartedAt) / 1000;
      }

      // Timer debugging:
      // console.log(`duration: ${duration}; currentChapterStartedAt: ${this.sharedStore.state.currentChapterStartedAt}; currentDurationSec: ${this.sharedStore.state.currentDurationSec}`);

      if (duration <= 0) {
        return;
      }

      const hours = Math.floor(duration / 3600);
      const minutes = Math.floor((duration % 3600) / 60);
      let seconds = Math.floor(duration % 60);

      if (seconds < 10) {
        seconds = `0${seconds}`;
      }

      if (hours > 0) {
        const paddedMinutes = minutes < 10 ? `0${minutes}` : minutes;
        return `${hours}:${paddedMinutes}:${seconds}`;
      }

      return `${minutes}:${seconds}`;
    },
    currentScreen() {
      return this.sharedStore.state.screens[this.sharedStore.state.currentScreenIndex];
    },
    currentItem() {
      return this.currentScreen.chapter;
    },
    currentItemType() {
      return this.currentScreen.type;
    },
    currentItemPosition() {
      return this.currentScreen.screenIndex;
    },
    itemsCount() {
      // Count unique chapters
      const chapterIndices = new Set(this.sharedStore.state.screens.map(s => s.screenIndex));
      return chapterIndices.size;
    },
    isLastChapter() {
      const screen = this.currentScreen;
      // Check if this is the last chapter's main screen or outro
      const lastScreenIndex = Math.max(...this.sharedStore.state.screens.map(s => s.screenIndex));
      return screen.screenIndex === lastScreenIndex;
    },
    questIntro() {
      return this.sharedStore.state.questIntro || "";
    },
    questName() {
      return this.sharedStore.state.questName || "Quest Intro";
    },
    isPaused() {
      return this.sharedStore.state.questPaused;
    },
    pauseButtonTitle() {
      return this.sharedStore.state.pauseRequested
        ? "Pause scheduled for next chapter (click to cancel)"
        : "Pause after current chapter";
    }
  },
  methods: {
    handlePauseClick() {
      if (this.sharedStore.state.pauseRequested) {
        // Cancel pause
        if (confirm("Do you want to cancel the pause?")) {
          this.sharedStore.cancelPause();
        }
      } else {
        // Request pause
        if (confirm("Do you want to pause after completing the current chapter?\n\nFinish this chapter and the pause will take effect. Timer will stop until you resume.")) {
          this.sharedStore.requestPause();
        }
      }
    },
    handleResume() {
      if (confirm("Are you sure you want to continue?\n\nThe timer will start and you won't be able to pause until you solve the next chapter.")) {
        this.currentTime = new Date();

        this.sharedStore.continueQuest().then(() => {
          this.sharedStore.nextScreen();
        });
      }
    },
    handleFullscreenChange() {
      this.fullScreen = !!document.fullscreenElement;
    },
    handleSidebarToggle(isExpanded) {
      this.sidebarExpanded = isExpanded;
    },
    handleScreenSelected(screenIndex) {
      if (screenIndex < this.sharedStore.state.screens.length && screenIndex >= 0) {
        this.sharedStore.setCurrentScreenIndex(screenIndex);
        this.scrollToTop();
      }
    },
    toggleFullScreen() {
      if (this.fullScreen) {
        this.exitFullScreen();
      } else {
        this.enterFullScreen();
      }
    },
    handleScreenFinished() {
      this.sharedStore.nextScreen();
    },
    scrollToTop() {
      this.$refs.questContent.scrollTo(0, 0);

      if(this.fullScreen) {
        this.$refs.container.scrollTo(0, 0);
      }
    },
    enterFullScreen() {
      const container = this.$refs.container;

      container.requestFullscreen({ navigationUI: "hide" }).catch((error) => {
        this.fullScreen = false;

        Sentry.captureException(error);
      });
    },
    exitFullScreen() {
      if (!document.fullscreenElement) {
        return;
      }

      document.exitFullscreen().catch((error) => {
        console.error(error)

        Sentry.captureException(error);
      })
    }
  },
  beforeMount() {
    this.sharedStore.setScreens(window.state.chapters);
  },
  watch: {
    'sharedStore.state.questPaused'(paused) {
      if (paused) {
        // Stop the timer when quest is paused
        if (this.stopwatchInterval) {
          clearInterval(this.stopwatchInterval);
          this.stopwatchInterval = null;
        }
      } else {
        // Resume the timer when quest continues
        if (!this.stopwatchInterval && !this.sharedStore.state.questFinished) {
          this.stopwatchInterval = setInterval(() => {
            this.currentTime = new Date();
          }, 1000);
        }
      }
    }
  },
  mounted() {
    this.sharedStore.setScrollToTopCallback(this.scrollToTop);

    document.addEventListener("fullscreenchange", this.handleFullscreenChange)

    if (!this.sharedStore.state.questFinished && !this.sharedStore.state.questPaused) {
      this.stopwatchInterval = setInterval(() => {
        this.currentTime = new Date();
      }, 1000);
    }
  },
  beforeDestroy() {
    document.removeEventListener("fullscreenchange", this.handleFullscreenChange)

    if (this.stopwatchInterval) {
      clearInterval(this.stopwatchInterval);
    }
  },
}
</script>

<style lang="scss">
.quest {
  $root: &;

  display: flex;
  height: 100vh;

  &__content {
    flex: 1;
    min-width: 0;
    height: 100vh;
    overflow-y: auto;
    padding-bottom: $px48;

    blockquote {
      padding-left: $px16;

      border-left-width: $px4;

      @include font-regular;
      font-size: $px16;

      strong {
        @include font-regular;
        font-weight: 700;
      }

      p + p {
        margin-top: $px12;
      }
    }
  }

  &__container {
    position: relative;
    padding: 0 $px24 $px64;

    @include breakpoint($sm) {
      padding-bottom: 0;
    }

    @include breakpoint($md) {
      padding-top: $px16;
    }
  }

  &__header {
    position: fixed;
    top: $px16;
    right: $px16;
    z-index: 100;
  }

  &:fullscreen {
    padding: 0;
    overflow-y: scroll;
    position: relative;
    z-index: 1;

    background-color: $white;
  }
}
</style>
